import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactFlow, {
  Node,
  Edge,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  MiniMap,
  ReactFlowProvider,
  Panel,
  Handle,
  Position,
  ConnectionMode,
} from 'reactflow';
import 'reactflow/dist/style.css';

// Global CSS injection for React Flow overrides
const globalFlowStyles = `
  <style>
    .react-flow__node {
      background: transparent !important;
      border: none !important;
    }
    .react-flow__handle {
      background: var(--color-gray-600) !important;
      border: 2px solid var(--color-primary) !important;
      width: 24px !important;
      height: 24px !important;
      min-width: 24px !important;
      min-height: 24px !important;
    }
    .react-flow__handle:hover {
      background: var(--color-primary-light) !important;
      border-color: var(--color-primary-light) !important;
    }
  </style>
`;
import {
  FiMessageSquare, FiSend, FiPhone, FiClock, FiDatabase,
  FiCpu, FiUsers, FiMail, FiPlay, FiPause, FiSave,
  FiDownload, FiUpload, FiPlus, FiSettings, FiZap, FiX,
  FiTerminal, FiAlertTriangle, FiInfo, FiRefreshCw, FiWifi,
  FiWifiOff, FiChevronUp, FiChevronDown, FiCircle, FiAlertCircle, FiCheckCircle
} from 'react-icons/fi';
import { BiBot, BiBrain } from 'react-icons/bi';
import { BsRobot } from 'react-icons/bs';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useSocket } from '../contexts/SocketContext';
import Toolbar from '../components/builder/Toolbar';

// Custom Node Types
const MessageNode = ({ data, isConnectable }: any) => (
  <motion.div
    initial={{ scale: 0.8, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    className="px-4 py-3 shadow-lg rounded-lg bg-[var(--color-bg-secondary)] border border-[var(--color-border-primary)] min-w-[200px] relative"
  >
    <Handle
      type="target"
      position={Position.Top}
      isConnectable={isConnectable}
    />
    <div className="flex items-center gap-2 text-[var(--color-text-primary)]">
      <FiMessageSquare size={16} />
      <div className="font-medium">{data.label}</div>
    </div>
    <div className="text-xs text-[var(--color-text-secondary)] mt-1">
      {data.actionName || data.message || 'Internal Notification'}
    </div>
    <Handle
      type="source"
      position={Position.Bottom}
      isConnectable={isConnectable}
    />
  </motion.div>
);

const BotResponseNode = ({ data, isConnectable }: any) => (
  <motion.div
    initial={{ scale: 0.8, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    className="px-4 py-3 shadow-lg rounded-lg bg-[var(--color-bg-secondary)] border border-[var(--color-border-primary)] min-w-[200px] relative"
  >
    <Handle
      type="target"
      position={Position.Top}
      isConnectable={isConnectable}
    />
    <div className="flex items-center gap-2 text-[var(--color-text-primary)]">
      <BiBot size={16} />
      <div className="font-medium">{data.label}</div>
    </div>
    <div className="text-xs text-[var(--color-text-secondary)] mt-1">{data.response || 'AI will respond...'}</div>
    <Handle
      type="source"
      position={Position.Bottom}
      isConnectable={isConnectable}
    />
  </motion.div>
);

const ConditionNode = ({ data, isConnectable }: any) => (
  <motion.div
    initial={{ scale: 0.8, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    className="px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r from-orange-500 to-red-500 border-2 border-white/20 min-w-[200px] relative"
  >
    <Handle
      type="target"
      position={Position.Top}
      isConnectable={isConnectable}
      style={{ background: '#555', width: 10, height: 10 }}
    />
    <div className="flex items-center gap-2 text-white">
      <FiZap size={16} />
      <div className="font-medium">{data.label}</div>
    </div>
    <div className="text-xs text-orange-100 mt-1">{data.condition || 'If condition...'}</div>
    <Handle
      type="source"
      position={Position.Bottom}
      id="true"
      isConnectable={isConnectable}
      style={{ left: '30%', background: 'var(--color-primary)', width: 10, height: 10 }}
    />
    <Handle
      type="source"
      position={Position.Bottom}
      id="false"
      isConnectable={isConnectable}
      style={{ left: '70%', background: 'var(--color-border-primary)', width: 10, height: 10 }}
    />
  </motion.div>
);

const ActionNode = ({ data, isConnectable }: any) => (
  <motion.div
    initial={{ scale: 0.8, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    className="px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 border-2 border-white/20 min-w-[200px] relative"
  >
    <Handle
      type="target"
      position={Position.Top}
      isConnectable={isConnectable}
      style={{ background: 'var(--color-border-primary)', width: 10, height: 10 }}
    />
    <div className="flex items-center gap-2 text-white">
      <FiSend size={16} />
      <div className="font-medium">{data.label}</div>
    </div>
    <div className="text-xs text-green-100 mt-1">{data.action || 'Perform action...'}</div>
    <Handle
      type="source"
      position={Position.Bottom}
      isConnectable={isConnectable}
      style={{ background: 'var(--color-border-primary)', width: 10, height: 10 }}
    />
  </motion.div>
);

const nodeTypes = {
  message: MessageNode,
  botResponse: BotResponseNode,
  condition: ConditionNode,
  action: ActionNode,
};

const FlowBuilder: React.FC = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNodeType, setSelectedNodeType] = useState<string>('message');
  const [isPlaying, setIsPlaying] = useState(false);
  const [showNodePanel, setShowNodePanel] = useState(true);
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [showPropertiesPanel, setShowPropertiesPanel] = useState(false);
  const [showConsole, setShowConsole] = useState(true);
  const [activeConsoleTab, setActiveConsoleTab] = useState('problems');
  const [consoleHeight, setConsoleHeight] = useState(200);
  const [apiStatus, setApiStatus] = useState<'connected' | 'connecting' | 'disconnected'>('connected');
  const [phoneStatus, setPhoneStatus] = useState<'connected' | 'connecting' | 'disconnected'>('connected');
  const [problems, setProblems] = useState<Array<{id: string, type: 'error' | 'warning' | 'info', message: string, node?: string}>>([]);
  const [outputs, setOutputs] = useState<Array<{id: string, timestamp: string, type: 'log' | 'error' | 'success', message: string}>>([]);
  const [debugLogs, setDebugLogs] = useState<Array<{id: string, timestamp: string, level: 'debug' | 'info' | 'warn' | 'error', message: string, data?: any}>>([]);
  const [flowId, setFlowId] = useState<string | null>(localStorage.getItem('assistable_flowId') || null);
  const [flowName, setFlowName] = useState<string>('');
  const [backendStatus, setBackendStatus] = useState<{ nodes: number; connections: number; status: string } | null>(null);
  const { socket } = useSocket();

  const nodeTemplates = [
    {
      type: 'message',
      label: 'Notification',
      icon: <FiMessageSquare size={20} />,
      color: 'from-blue-500 to-cyan-500',
      description: 'Send internal notification'
    },
    {
      type: 'botResponse',
      label: 'Bot Response',
      icon: <BiBot size={20} />,
      color: 'from-purple-500 to-pink-500',
      description: 'AI generates response'
    },
    {
      type: 'condition',
      label: 'Condition',
      icon: <FiZap size={20} />,
      color: 'from-orange-500 to-red-500',
      description: 'Branch conversation flow'
    },
    {
      type: 'action',
      label: 'Action',
      icon: <FiSend size={20} />,
      color: 'from-green-500 to-emerald-500',
      description: 'Perform external action'
    }
  ];

  const onConnect = useCallback(
    (params: Connection) => {
      // Enhanced connection with labels for condition nodes
      const newEdge: any = {
        ...params,
        id: `e${params.source}-${params.target}`,
        animated: true,
        style: { stroke: 'var(--color-border-primary)', strokeWidth: 2 },
      };
      
      // Add label for condition node connections
      const sourceNode = nodes.find(n => n.id === params.source);
      if (sourceNode?.type === 'condition') {
        if (params.sourceHandle === 'true') {
          newEdge.label = 'Yes';
          newEdge.style = { ...newEdge.style, stroke: 'var(--color-primary)' };
        } else if (params.sourceHandle === 'false') {
          newEdge.label = 'No';
          newEdge.style = { ...newEdge.style, stroke: 'var(--color-border-primary)' };
        }
      }
      
      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges, nodes]
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
      const type = event.dataTransfer.getData('application/reactflow');

      if (typeof type === 'undefined' || !type || !reactFlowBounds) {
        return;
      }

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNode: Node = {
        id: `${type}_${Date.now()}`,
        type,
        position,
        data: { 
          label: type === 'message' ? 'Internal Notification' : `${type.charAt(0).toUpperCase() + type.slice(1)} Node`,
          message: type === 'message' ? 'Send a notification to someone other than the contact' : undefined,
          actionName: type === 'message' ? 'Internal Notification to Business Owner - Ana' : undefined,
          notificationType: type === 'message' ? 'Notification' : undefined,
          redirectPage: type === 'message' ? 'Contact' : undefined,
          userType: type === 'message' ? 'Particular User' : undefined,
          selectedUser: type === 'message' ? '' : undefined,
          response: type === 'botResponse' ? 'AI response...' : undefined,
          condition: type === 'condition' ? 'If condition...' : undefined,
          action: type === 'action' ? 'Perform action...' : undefined,
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, setNodes]
  );

  const onDragStart = (event: React.DragEvent, nodeType: string) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  const simulateFlow = async () => {
    if (isPlaying) {
      setIsPlaying(false);
      toast('Flow simulation stopped', { icon: 'ℹ️' });
      return;
    }

    if (nodes.length === 0) {
      toast.error('Cannot simulate empty flow');
      return;
    }
    
    // Validate flow connectivity
    const startNodes = nodes.filter(node => !edges.some(edge => edge.target === node.id));
    if (startNodes.length === 0) {
      toast.error('No start node found. Ensure at least one node has no incoming connections.');
      return;
    }
    
    if (startNodes.length > 1) {
      toast('Multiple start nodes detected. Using the first one.', { icon: '⚠️' });
    }

    setIsPlaying(true);
    toast.success('Flow simulation started!');
    addOutput('log', 'Starting flow simulation...');
    addDebugLog('info', 'Flow execution initiated', { nodes: nodes.length, edges: edges.length, startNode: startNodes[0].id });

    try {
      let effectiveFlowId = flowId;
      if (!effectiveFlowId) {
        // Save first to get an ID
        const saveResp = await api.post('/flows/save', {
          name: `Flow ${new Date().toLocaleString()}`,
          description: `Flow with ${nodes.length} nodes and ${edges.length} connections`,
          nodes,
          connections: edges,
          isActive: true
        });
        const saved = saveResp.data?.flow;
        effectiveFlowId = saved?.id;
        if (effectiveFlowId) {
          setFlowId(effectiveFlowId);
          setFlowName(saved?.name || '');
          localStorage.setItem('assistable_flowId', effectiveFlowId);
        }
      }

      if (effectiveFlowId) {
        const executeResponse = await api.post(`/flows/test`, { flowId: effectiveFlowId });
        const result = executeResponse.data;
          
          // Animate through the execution steps
          if (result.execution && result.execution.steps) {
            for (let i = 0; i < result.execution.steps.length; i++) {
              const step = result.execution.steps[i];
              
              setTimeout(() => {
                // Highlight current node
                setNodes((nds) =>
                  nds.map((n) =>
                    n.id === step.nodeId
                      ? { ...n, style: { ...n.style, boxShadow: '0 0 20px #10B981', border: '2px solid #10B981' } }
                      : n
                  )
                );
                
                // Add step output to console
                addOutput('log', `Executing ${step.nodeType}: ${step.nodeId}`);
                if (step.output) {
                  addDebugLog('debug', `Node output: ${JSON.stringify(step.output)}`, { nodeId: step.nodeId });
                }
                
                // Highlight edges leading to this node
                setEdges((eds) =>
                  eds.map((e) =>
                    e.target === step.nodeId
                      ? { ...e, animated: true, style: { ...e.style, stroke: '#10B981', strokeWidth: 3 } }
                      : e
                  )
                );
                
                setTimeout(() => {
                  setNodes((nds) =>
                    nds.map((n) =>
                      n.id === step.nodeId
                        ? { ...n, style: { ...n.style, boxShadow: 'none', border: 'none' } }
                        : n
                    )
                  );
                  setEdges((eds) =>
                    eds.map((e) =>
                      e.target === step.nodeId
                        ? { ...e, animated: false, style: { ...e.style, strokeWidth: 2 } }
                        : e
                    )
                  );
                }, 1000);
              }, i * 1500);
            }
            
            setTimeout(() => {
              setIsPlaying(false);
              toast.success(`Flow executed successfully!`);
              addOutput('success', `Flow execution completed`);
              addDebugLog('info', 'Flow execution finished successfully', result.execution);
            }, result.execution.steps.length * 1500);
          } else {
            // Fallback animation
            nodes.forEach((node, index) => {
              setTimeout(() => {
                setNodes((nds) =>
                  nds.map((n) =>
                    n.id === node.id
                      ? { ...n, style: { ...n.style, boxShadow: '0 0 20px #10B981' } }
                      : n
                  )
                );
                setTimeout(() => {
                  setNodes((nds) =>
                    nds.map((n) =>
                      n.id === node.id
                        ? { ...n, style: { ...n.style, boxShadow: 'none' } }
                        : n
                    )
                  );
                }, 1000);
              }, index * 1500);
            });
            
            setTimeout(() => {
              setIsPlaying(false);
              toast.success('Flow simulation completed!');
              addOutput('success', 'Flow simulation completed successfully');
              addDebugLog('info', 'Fallback simulation finished', { nodeCount: nodes.length });
            }, nodes.length * 1500);
          }
      } else {
        throw new Error('Failed to determine flowId');
      }
    } catch (error) {
      console.error('Flow simulation error:', error);
      setIsPlaying(false);
      addOutput('error', `Flow simulation error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      addProblem('error', 'Flow execution failed - using offline mode', 'system');
      
      // Fallback to visual simulation only
      toast('Using offline simulation mode', { icon: '⚠️' });
      nodes.forEach((node, index) => {
        setTimeout(() => {
          setNodes((nds) =>
            nds.map((n) =>
              n.id === node.id
                ? { ...n, style: { ...n.style, boxShadow: '0 0 20px #10B981' } }
                : n
            )
          );
          setTimeout(() => {
            setNodes((nds) =>
              nds.map((n) =>
                n.id === node.id
                  ? { ...n, style: { ...n.style, boxShadow: 'none' } }
                  : n
              )
            );
          }, 1000);
        }, index * 1500);
      });
      
      setTimeout(() => {
        setIsPlaying(false);
        toast.success('Offline simulation completed!');
        addOutput('success', 'Offline simulation completed');
        addDebugLog('info', 'Offline simulation finished', { nodeCount: nodes.length });
      }, nodes.length * 1500);
    }
  };

  const saveFlow = async () => {
    if (nodes.length === 0) {
      toast.error('Cannot save empty flow');
      return;
    }

    const payload: any = {
      flowId: flowId || undefined,
      name: flowName || `Flow ${new Date().toLocaleString()}`,
      description: `Flow with ${nodes.length} nodes and ${edges.length} connections`,
      nodes,
      connections: edges,
      isActive: true
    };

    try {
      const response = await api.post('/flows/save', payload);
      const saved = response.data?.flow;
      if (saved?.id) {
        setFlowId(saved.id);
        setFlowName(saved.name || '');
        localStorage.setItem('assistable_flowId', saved.id);
      }
      toast.success('Flow saved');
      addOutput('success', 'Flow saved to server');
      addDebugLog('info', 'Flow save operation completed', { nodeCount: nodes.length, edgeCount: edges.length, flowId: saved?.id });
      localStorage.setItem('assistable_flow', JSON.stringify({ nodes, edges }));
    } catch (error) {
      console.error('Save flow error:', error);
      addOutput('error', `Save error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      addProblem('warning', 'Failed to save to server - using local storage', 'system');
      // Fallback to localStorage
      localStorage.setItem('assistable_flow', JSON.stringify({ nodes, edges }));
      toast.success('Flow saved locally!');
    }
  };

  const loadFlow = async () => {
    try {
      let effectiveFlowId = flowId || localStorage.getItem('assistable_flowId');
      if (!effectiveFlowId) {
        effectiveFlowId = window.prompt('Enter Flow ID to load') || '';
      }
      if (!effectiveFlowId) {
        toast.error('Flow ID is required');
        return;
      }
      const resp = await api.get(`/flows/${effectiveFlowId}`);
      const flow = resp.data?.flow;
      if (!flow) throw new Error('Flow not found');
      setNodes(flow.nodes || []);
      setEdges(flow.edges || flow.connections || []);
      setFlowId(flow.id);
      setFlowName(flow.name || '');
      localStorage.setItem('assistable_flowId', flow.id);
      toast.success('Flow loaded');
    } catch (e) {
      const savedFlow = localStorage.getItem('assistable_flow');
      if (savedFlow) {
        const flowData = JSON.parse(savedFlow);
        setNodes(flowData.nodes || []);
        setEdges(flowData.edges || []);
        toast('Loaded local flow backup', { icon: '🗂️' });
      } else {
        toast.error('Failed to load flow');
      }
    }
  };

  const clearFlow = async () => {
    try {
      if (flowId) {
        await api.delete(`/flows/${flowId}/draft`);
      }
    } catch (e) {
      // ignore
    }
    setNodes([]);
    setEdges([]);
    setSelectedNode(null);
    setShowPropertiesPanel(false);
    toast('Flow cleared', { icon: 'ℹ️' });
  };

  const onNodeClick = useCallback((_event: any, node: Node) => {
    setSelectedNode(node);
    setShowPropertiesPanel(true);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setShowPropertiesPanel(false);
  }, []);

  const updateNodeData = (nodeId: string, newData: any) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, ...newData } }
          : node
      )
    );
  };

  const syncNotification = async (nodeId: string, message: string) => {
    try {
      if (!flowId) return; // will sync on save
      await api.post('/flows/notification', { flowId, nodeId, message });
    } catch (e) {
      addProblem('warning', 'Failed to sync notification to backend', nodeId);
    }
  };

  const syncBotResponse = async (nodeId: string, prompt: string) => {
    try {
      if (!flowId) return;
      await api.post('/flows/bot-response', { flowId, nodeId, prompt });
    } catch (e) {
      addProblem('warning', 'Failed to sync bot response to backend', nodeId);
    }
  };

  const syncCondition = async (nodeId: string, conditionType: string, rules: any = {}) => {
    try {
      if (!flowId) return;
      await api.post('/flows/condition', { flowId, nodeId, conditionType, rules });
    } catch (e) {
      addProblem('warning', 'Failed to sync condition to backend', nodeId);
    }
  };

  const syncAction = async (nodeId: string, actionType: string, parameters: any = {}) => {
    try {
      if (!flowId) return;
      await api.post('/flows/action', { flowId, nodeId, actionType, parameters });
    } catch (e) {
      addProblem('warning', 'Failed to sync action to backend', nodeId);
    }
  };

  // Console utility functions
  const addProblem = (type: 'error' | 'warning' | 'info', message: string, node?: string) => {
    const newProblem = {
      id: Date.now().toString(),
      type,
      message,
      node
    };
    setProblems(prev => [newProblem, ...prev.slice(0, 99)]); // Keep last 100
  };

  const addOutput = (type: 'log' | 'error' | 'success', message: string) => {
    const newOutput = {
      id: Date.now().toString(),
      timestamp: new Date().toLocaleTimeString(),
      type,
      message
    };
    setOutputs(prev => [newOutput, ...prev.slice(0, 99)]); // Keep last 100
  };

  const addDebugLog = (level: 'debug' | 'info' | 'warn' | 'error', message: string, data?: any) => {
    const newLog = {
      id: Date.now().toString(),
      timestamp: new Date().toLocaleTimeString(),
      level,
      message,
      data
    };
    setDebugLogs(prev => [newLog, ...prev.slice(0, 99)]); // Keep last 100
  };

  const refreshApiConnection = async () => {
    setApiStatus('connecting');
    addOutput('log', 'Refreshing API connection...');
    try {
      const response = await api.get('/health');
      const data = response.data;
      
      if (data) {
        setApiStatus('connected');
        addOutput('success', 'API connection established');
      } else {
        throw new Error('API connection failed');
      }
    } catch (error) {
      setApiStatus('disconnected');
      addOutput('error', 'Failed to connect to API');
      addProblem('error', 'API connection failed', 'system');
    }
  };

  // Stats polling and WS subscription
  React.useEffect(() => {
    let interval: any;
    const subscribe = () => {
      if (socket && flowId) {
        const eventName = `flows:${flowId}:updates`;
        const handler = (_payload: any) => {
          // Refresh stats on updates
          api.get(`/flows/${flowId}/stats`).then((r) => setBackendStatus(r.data)).catch(() => {});
        };
        socket.on(eventName, handler);
        return () => {
          socket.off(eventName, handler);
        };
      }
      return () => {};
    };

    (async () => {
      if (flowId) {
        try {
          const r = await api.get(`/flows/${flowId}/stats`);
          setBackendStatus(r.data);
        } catch {}
        interval = setInterval(async () => {
          try {
            const r = await api.get(`/flows/${flowId}/stats`);
            setBackendStatus(r.data);
          } catch {}
        }, 5000);
      }
    })();

    const unsub = subscribe();
    return () => {
      if (interval) clearInterval(interval);
      unsub();
    };
  }, [socket, flowId]);

  const exportFlow = async () => {
    if (!flowId) {
      toast.error('Save the flow first');
      return;
    }
    try {
      const response = await api.get(`/flows/${flowId}/export`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `flow-${flowId}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Exported');
    } catch (e) {
      toast.error('Export failed');
    }
  };

  const refreshPhoneConnection = async () => {
    setPhoneStatus('connecting');
    addOutput('log', 'Refreshing phone connection...');
    // Simulate phone connection
    setTimeout(() => {
      setPhoneStatus('connected');
      addOutput('success', 'Phone service connected (Twilio)');
    }, 1500);
  };

  // Runtime style validator: ban white backgrounds on react-flow nodes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (process.env.NODE_ENV === 'production') return;
    const els = document.querySelectorAll('.react-flow__node');
    els.forEach((el) => {
      const bg = getComputedStyle(el as Element).backgroundColor?.toLowerCase();
      if (bg === '#ffffff' || bg === 'rgb(255, 255, 255)') {
        throw new Error('Design tokens required: white background detected on node');
      }
    });
  }, [nodes]);

  // Console Component
  const Console = () => (
    <AnimatePresence>
      {showConsole && (
        <motion.div
          initial={{ height: 0 }}
          animate={{ height: consoleHeight }}
          exit={{ height: 0 }}
          className="border-t border-gray-700 flex-shrink-0 relative"
          style={{
            background: 'rgba(15, 23, 42, 0.98)',
            backdropFilter: 'blur(20px)',
          }}
        >
          {/* Enhanced Resize Handle - Moved to top with better UX */}
          <div
            className="absolute -top-2 left-0 right-0 h-4 cursor-ns-resize group flex items-center justify-center z-50 hover:bg-gradient-to-b hover:from-purple-500/10 hover:to-transparent transition-all duration-200"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              
              const startY = e.clientY;
              const startHeight = consoleHeight;
              
              const handleMouseMove = (e: MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                const maxHeight = Math.min(500, window.innerHeight * 0.6);
                const newHeight = Math.max(160, Math.min(maxHeight, startHeight - (e.clientY - startY)));
                setConsoleHeight(newHeight);
              };
              
              const handleMouseUp = (e: MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                document.removeEventListener('mousemove', handleMouseMove, true);
                document.removeEventListener('mouseup', handleMouseUp, true);
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
              };
              
              document.body.style.cursor = 'ns-resize';
              document.body.style.userSelect = 'none';
              document.addEventListener('mousemove', handleMouseMove, { capture: true, passive: false });
              document.addEventListener('mouseup', handleMouseUp, { capture: true, passive: false });
            }}
          >
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-gray-800/60 backdrop-blur-sm border border-gray-600/50 group-hover:border-purple-500/50 transition-all duration-200">
              <div className="w-6 h-0.5 bg-gray-500 rounded-full group-hover:bg-purple-400 transition-colors" />
              <span className="text-xs text-gray-400 group-hover:text-purple-300 font-medium">Resize</span>
              <div className="w-6 h-0.5 bg-gray-500 rounded-full group-hover:bg-purple-400 transition-colors" />
            </div>
          </div>

          <div className="flex h-full max-h-full">
            {/* Console Tabs and Content - Optimized layout */}
            <div className="flex-1 flex flex-col min-w-0 min-h-0">
              {/* Console Header - Enhanced visual hierarchy */}
              <div className="flex items-center justify-between px-6 py-3 border-b border-gray-700 shrink-0 bg-gradient-to-r from-gray-900/50 to-gray-800/30">
                <div className="flex items-center gap-1 min-w-0">
                  <div className="flex items-center gap-3">
                    {['problems', 'output', 'debug'].map((tab) => (
                      <motion.button
                        key={tab}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setActiveConsoleTab(tab)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex items-center gap-2 ${
                          activeConsoleTab === tab
                            ? 'bg-gradient-to-r from-purple-500/20 to-blue-500/20 text-purple-300 border border-purple-500/30 shadow-lg shadow-purple-500/10'
                            : 'text-gray-400 hover:text-white hover:bg-gray-700/50 border border-transparent'
                        }`}
                      >
                        {tab === 'problems' && <FiAlertTriangle size={16} />}
                        {tab === 'output' && <FiTerminal size={16} />}
                        {tab === 'debug' && <FiInfo size={16} />}
                        <span className="capitalize font-semibold">{tab}</span>
                        {tab === 'problems' && problems.length > 0 && (
                          <span className="px-2 py-0.5 bg-red-500/20 text-red-300 rounded-full text-xs font-bold border border-red-500/30">
                            {problems.length}
                          </span>
                        )}
                      </motion.button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => {
                      if (activeConsoleTab === 'problems') setProblems([]);
                      if (activeConsoleTab === 'output') setOutputs([]);
                      if (activeConsoleTab === 'debug') setDebugLogs([]);
                    }}
                    className="p-2 rounded-lg bg-gray-700/80 text-gray-300 hover:text-white hover:bg-gray-600 transition-all duration-200 border border-gray-600/50"
                  >
                    <FiX size={16} />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowConsole(false)}
                    className="p-2 rounded-lg bg-gray-700/80 text-gray-300 hover:text-white hover:bg-gray-600 transition-all duration-200 border border-gray-600/50"
                  >
                    <FiChevronDown size={16} />
                  </motion.button>
                </div>
              </div>

              {/* Console Content - Enhanced spacing and readability */}
              <div className="flex-1 overflow-auto px-6 py-4 min-h-0">
                {activeConsoleTab === 'problems' && (
                  <div className="space-y-3">
                    {problems.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <FiCheckCircle size={48} className="text-green-500/30 mb-4" />
                        <div className="text-gray-400 text-lg font-medium">All Clear!</div>
                        <div className="text-gray-500 text-sm mt-1">No problems detected in your flow</div>
                      </div>
                    ) : (
                      problems.map((problem) => (
                        <motion.div
                          key={problem.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="flex items-start gap-4 p-4 rounded-xl hover:bg-gray-800/50 cursor-pointer border border-gray-700/50 hover:border-gray-600/50 transition-all duration-200 group"
                        >
                          <div className={`w-3 h-3 rounded-full mt-1 shrink-0 shadow-lg ${
                            problem.type === 'error' ? 'bg-red-500 shadow-red-500/50' :
                            problem.type === 'warning' ? 'bg-yellow-500 shadow-yellow-500/50' : 'bg-blue-500 shadow-blue-500/50'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <span className={`text-sm leading-relaxed font-medium ${
                              problem.type === 'error' ? 'text-red-300' :
                              problem.type === 'warning' ? 'text-yellow-300' : 'text-blue-300'
                            }`}>
                              {problem.message}
                            </span>
                            {problem.node && (
                              <span className="ml-3 text-xs text-gray-400 bg-gray-700/80 px-3 py-1 rounded-lg whitespace-nowrap border border-gray-600/50">
                                {problem.node}
                              </span>
                            )}
                          </div>
                        </motion.div>
                      ))
                    )}
                  </div>
                )}

                {activeConsoleTab === 'output' && (
                  <div className="space-y-2 font-mono text-sm">
                    {outputs.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <FiTerminal size={48} className="text-gray-500/30 mb-4" />
                        <div className="text-gray-400 text-lg font-medium">No Output</div>
                        <div className="text-gray-500 text-sm mt-1">Output logs will appear here</div>
                      </div>
                    ) : (
                      outputs.map((output) => (
                        <motion.div
                          key={output.id}
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex items-start gap-4 p-3 hover:bg-gray-800/50 rounded-lg transition-all duration-200"
                        >
                          <span className="text-xs text-gray-500 w-20 shrink-0 mt-0.5 font-medium">{output.timestamp}</span>
                          <div className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 shadow-lg ${
                            output.type === 'error' ? 'bg-red-500 shadow-red-500/50' :
                            output.type === 'success' ? 'bg-green-500 shadow-green-500/50' : 'bg-blue-500 shadow-blue-500/50'
                          }`} />
                          <span className={`flex-1 leading-relaxed ${
                            output.type === 'error' ? 'text-red-300' :
                            output.type === 'success' ? 'text-green-300' : 'text-gray-300'
                          }`}>
                            {output.message}
                          </span>
                        </motion.div>
                      ))
                    )}
                  </div>
                )}

                {activeConsoleTab === 'debug' && (
                  <div className="space-y-3 font-mono text-sm">
                    {debugLogs.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <FiInfo size={48} className="text-gray-500/30 mb-4" />
                        <div className="text-gray-400 text-lg font-medium">No Debug Info</div>
                        <div className="text-gray-500 text-sm mt-1">Debug logs will appear here</div>
                      </div>
                    ) : (
                      debugLogs.map((log) => (
                        <motion.div
                          key={log.id}
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex items-start gap-4 p-3 hover:bg-gray-800/50 rounded-lg transition-all duration-200"
                        >
                          <span className="text-xs text-gray-500 w-20 shrink-0 mt-0.5 font-medium">{log.timestamp}</span>
                          <span className={`text-xs w-16 shrink-0 mt-0.5 font-bold ${
                            log.level === 'error' ? 'text-red-400' :
                            log.level === 'warn' ? 'text-yellow-400' :
                            log.level === 'info' ? 'text-blue-400' : 'text-gray-400'
                          }`}>
                            [{log.level.toUpperCase()}]
                          </span>
                          <div className="flex-1 min-w-0">
                            <span className="text-gray-300 leading-relaxed block">{log.message}</span>
                            {log.data && (
                              <pre className="text-xs text-gray-500 mt-3 bg-gray-900/50 p-4 rounded-lg overflow-x-auto border border-gray-700/50">
                                {JSON.stringify(log.data, null, 2)}
                              </pre>
                            )}
                          </div>
                        </motion.div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Connection Status Panel - Optimized layout */}
            <div className="w-80 border-l border-gray-700 shrink-0 flex flex-col bg-gradient-to-b from-gray-900/30 to-gray-800/20">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700/50 shrink-0">
                <h4 className="text-lg font-bold text-gray-100 flex items-center gap-3">
                  <FiZap size={20} className="text-purple-400" />
                  Connections
                </h4>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse shadow-lg shadow-green-500/50"></div>
                  <span className="text-xs text-gray-400 font-medium">Live</span>
                </div>
              </div>

              {/* Optimized connections content with better spacing */}
              <div className="flex-1 px-6 py-4 space-y-6 min-h-0 overflow-y-auto">
                {/* API Connection - Enhanced visual design */}
                <div className="space-y-3 p-4 rounded-xl bg-gradient-to-br from-gray-800/60 to-gray-700/40 border border-gray-600/50 hover:border-gray-500/50 transition-all duration-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-4 h-4 rounded-full shrink-0 shadow-xl transition-all duration-300 ${
                        apiStatus === 'connected' ? 'bg-green-500 shadow-green-500/60 animate-pulse' :
                        apiStatus === 'connecting' ? 'bg-yellow-500 shadow-yellow-500/60 animate-pulse' : 
                        'bg-red-500 shadow-red-500/60'
                      }`} />
                      <div>
                        <span className="text-sm font-semibold text-gray-200">API Server</span>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {apiStatus === 'connected' && 'Backend connected'}
                          {apiStatus === 'connecting' && 'Establishing connection...'}
                          {apiStatus === 'disconnected' && 'Connection failed'}
                        </div>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={refreshApiConnection}
                      disabled={apiStatus === 'connecting'}
                      className="p-2.5 rounded-xl bg-gray-700/80 text-gray-300 hover:text-white hover:bg-gray-600/80 transition-all duration-200 disabled:opacity-50 border border-gray-600/50"
                    >
                      <FiRefreshCw size={16} className={apiStatus === 'connecting' ? 'animate-spin' : ''} />
                    </motion.button>
                  </div>
                </div>

                {/* Phone Connection - Enhanced visual design */}
                <div className="space-y-3 p-4 rounded-xl bg-gradient-to-br from-gray-800/60 to-gray-700/40 border border-gray-600/50 hover:border-gray-500/50 transition-all duration-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-4 h-4 rounded-full shrink-0 shadow-xl transition-all duration-300 ${
                        phoneStatus === 'connected' ? 'bg-green-500 shadow-green-500/60 animate-pulse' :
                        phoneStatus === 'connecting' ? 'bg-yellow-500 shadow-yellow-500/60 animate-pulse' : 
                        'bg-red-500 shadow-red-500/60'
                      }`} />
                      <div>
                        <span className="text-sm font-semibold text-gray-200">Phone Service</span>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {phoneStatus === 'connected' && 'Twilio active'}
                          {phoneStatus === 'connecting' && 'Connecting to Twilio...'}
                          {phoneStatus === 'disconnected' && 'Service offline'}
                        </div>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={refreshPhoneConnection}
                      disabled={phoneStatus === 'connecting'}
                      className="p-2.5 rounded-xl bg-gray-700/80 text-gray-300 hover:text-white hover:bg-gray-600/80 transition-all duration-200 disabled:opacity-50 border border-gray-600/50"
                    >
                      <FiRefreshCw size={16} className={phoneStatus === 'connecting' ? 'animate-spin' : ''} />
                    </motion.button>
                  </div>
                </div>

                {/* Connection Settings - Enhanced button design */}
                <div className="pt-4 border-t border-gray-700/50">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      // Minimal connection settings panel using alert modal
                      const apiBase = window.location.origin;
                      const wsBase = apiBase.replace(/^http/, 'ws');
                      toast.remove();
                      toast(
                        () => (
                          <div className="text-sm">
                            <div className="font-semibold mb-2">Connection Settings</div>
                            <div className="mb-1 text-gray-300">API: <span className="text-gray-100">{apiBase}</span></div>
                            <div className="mb-3 text-gray-300">WebSocket: <span className="text-gray-100">{wsBase}</span></div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  refreshApiConnection();
                                  toast.success('Refreshed API connection');
                                }}
                                className="px-3 py-1 bg-blue-600 text-white rounded"
                              >
                                Refresh API
                              </button>
                              <button
                                onClick={() => {
                                  refreshPhoneConnection();
                                  toast.success('Refreshing phone connection');
                                }}
                                className="px-3 py-1 bg-purple-600 text-white rounded"
                              >
                                Refresh Phone
                              </button>
                            </div>
                          </div>
                        ),
                        { duration: 4000 }
                      );
                    }}
                    className="w-full p-3.5 text-sm bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl hover:from-purple-700 hover:to-blue-700 text-white font-semibold transition-all duration-300 flex items-center gap-3 justify-center shadow-xl hover:shadow-2xl border border-purple-500/30 hover:border-purple-400/50"
                  >
                    <FiSettings size={16} />
                    Connection Settings
                  </motion.button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Properties Panel Component
  const PropertiesPanel = () => (
    <AnimatePresence>
      {showPropertiesPanel && selectedNode && (
        <motion.div
          initial={{ x: 300, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 300, opacity: 0 }}
          className="w-80 p-6 relative z-10 border-l border-gray-700 flex-shrink-0 overflow-y-auto"
          style={{
            background: 'rgba(15, 23, 42, 0.95)',
            backdropFilter: 'blur(20px)',
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-white">
                {selectedNode.type === 'message' ? 'Internal Notification' : 'Node Properties'}
              </h3>
              {selectedNode.type === 'message' && (
                <p className="text-sm text-gray-400 mt-1">
                  Send a notification to someone other than the contact
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {selectedNode.type === 'message' && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-3 py-1 text-sm bg-blue-500/20 border border-blue-500/30 rounded-lg text-blue-400 hover:bg-blue-500/30 transition-colors flex items-center gap-1"
                >
                  <FiInfo size={14} />
                  Learn More
                </motion.button>
              )}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setShowPropertiesPanel(false)}
                className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:text-white transition-colors"
              >
                <FiX size={16} />
              </motion.button>
            </div>
          </div>

          <div className="space-y-6">
            {/* Node Type Badge */}
            <div className="p-3 rounded-lg bg-gray-800/50 border border-gray-700">
              <div className="flex items-center gap-3">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                  className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500"
                >
                  {selectedNode.type === 'message' && <FiMessageSquare size={16} className="text-white" />}
                  {selectedNode.type === 'botResponse' && <BiBot size={16} className="text-white" />}
                  {selectedNode.type === 'condition' && <FiZap size={16} className="text-white" />}
                  {selectedNode.type === 'action' && <FiSend size={16} className="text-white" />}
                  {selectedNode.type === 'question' && <FiMessageSquare size={16} className="text-white" />}
                  {selectedNode.type === 'tool' && <FiCpu size={16} className="text-white" />}
                </motion.div>
                <div>
                  <p className="text-white font-medium capitalize">{selectedNode.type} Node</p>
                  <p className="text-xs text-gray-400">ID: {selectedNode.id}</p>
                </div>
              </div>
            </div>

            {/* Basic Properties */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Node Label
                </label>
                <input
                  type="text"
                  value={selectedNode.data.label || ''}
                  onChange={(e) => updateNodeData(selectedNode.id, { label: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 transition-colors"
                  placeholder="Enter node label..."
                />
              </div>

              {/* Node-specific fields */}
              {selectedNode.type === 'message' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Action Name
                    </label>
                    <input
                      type="text"
                      value={selectedNode.data.actionName || ''}
                      onChange={(e) => updateNodeData(selectedNode.id, { actionName: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 transition-colors"
                      placeholder="Internal Notification to Business Owner - Ana"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Type of Notification
                    </label>
                    <select
                      value={selectedNode.data.notificationType || 'Notification'}
                      onChange={(e) => updateNodeData(selectedNode.id, { notificationType: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 transition-colors"
                    >
                      <option value="Notification">Notification</option>
                      <option value="Email">Email</option>
                      <option value="SMS">SMS</option>
                      <option value="WhatsApp">WhatsApp</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Message Content
                    </label>
                    <textarea
                      value={selectedNode.data.message || ''}
                      onChange={(e) => updateNodeData(selectedNode.id, { message: e.target.value })}
                      onBlur={(e) => syncNotification(selectedNode.id, e.target.value)}
                      rows={6}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 transition-colors resize-none"
                      placeholder="Hi Dan, {{contact.first_name}} {{contact.phone}} needs some assistance with their enquiry can you please have a look at this conversation and take over from the AI Assistant.

Thank you."
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      186 characters | 28 words
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Redirect Page
                    </label>
                    <select
                      value={selectedNode.data.redirectPage || 'Contact'}
                      onChange={(e) => updateNodeData(selectedNode.id, { redirectPage: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 transition-colors"
                    >
                      <option value="Contact">Contact</option>
                      <option value="Conversation">Conversation</option>
                      <option value="Opportunity">Opportunity</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      To User Type
                    </label>
                    <select
                      value={selectedNode.data.userType || 'Particular User'}
                      onChange={(e) => updateNodeData(selectedNode.id, { userType: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 transition-colors"
                    >
                      <option value="Particular User">Particular User</option>
                      <option value="All Users">All Users</option>
                      <option value="User assigned to the contact">User assigned to the contact</option>
                    </select>
                  </div>

                  {selectedNode.data.userType === 'Particular User' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Select User
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={selectedNode.data.selectedUser || ''}
                          onChange={(e) => updateNodeData(selectedNode.id, { selectedUser: e.target.value })}
                          className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 transition-colors"
                          placeholder="Type to search"
                        />
                        {/* User suggestions dropdown would go here */}
                        <div className="absolute top-full left-0 right-0 bg-gray-800 border border-gray-600 rounded-lg mt-1 max-h-40 overflow-y-auto z-50 hidden">
                          <div className="p-2 hover:bg-gray-700 cursor-pointer text-sm">ana richmond</div>
                          <div className="p-2 hover:bg-gray-700 cursor-pointer text-sm">Charl Account</div>
                          <div className="p-2 hover:bg-gray-700 cursor-pointer text-sm">Daniel Sivyer</div>
                          <div className="p-2 hover:bg-gray-700 cursor-pointer text-sm">eddy g</div>
                          <div className="p-2 hover:bg-gray-700 cursor-pointer text-sm">Jaime Thomson</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selectedNode.type === 'botResponse' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      AI Response Template
                    </label>
                    <textarea
                      value={selectedNode.data.response || ''}
                      onChange={(e) => updateNodeData(selectedNode.id, { response: e.target.value })}
                      onBlur={(e) => syncBotResponse(selectedNode.id, e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 transition-colors resize-none"
                      placeholder="Enter AI response template..."
                    />
                  </div>
                </div>
              )}

              {selectedNode.type === 'condition' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Condition Expression
                    </label>
                    <input
                      type="text"
                      value={selectedNode.data.condition || ''}
                      onChange={(e) => updateNodeData(selectedNode.id, { condition: e.target.value })}
                      onBlur={(e) => syncCondition(selectedNode.id, e.target.value, selectedNode.data?.config?.rules || {})}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 transition-colors"
                      placeholder="e.g., intent == 'support'"
                    />
                  </div>
                  <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                    <p className="text-xs text-blue-300">
                      💡 Use variables like: intent, user_input, confidence_score
                    </p>
                  </div>
                </div>
              )}

              {selectedNode.type === 'action' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Action Configuration
                    </label>
                    <textarea
                      value={selectedNode.data.action || ''}
                      onChange={(e) => updateNodeData(selectedNode.id, { action: e.target.value })}
                      onBlur={(e) => syncAction(selectedNode.id, e.target.value, selectedNode.data?.config?.parameters || {})}
                      rows={3}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 transition-colors resize-none"
                      placeholder="Enter action details..."
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-4 border-t border-gray-700">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setNodes((nds) => nds.filter((n) => n.id !== selectedNode.id));
                  setEdges((eds) => eds.filter((e) => e.source !== selectedNode.id && e.target !== selectedNode.id));
                  setShowPropertiesPanel(false);
                  setSelectedNode(null);
                  toast('Node deleted', { icon: '🗑️' });
                }}
                className="flex-1 py-2 px-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 hover:bg-red-500/30 transition-colors text-sm font-medium"
              >
                Delete
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  const newNode = {
                    ...selectedNode,
                    id: `${selectedNode.type}_${Date.now()}`,
                    position: {
                      x: selectedNode.position.x + 50,
                      y: selectedNode.position.y + 50,
                    },
                    data: {
                      ...selectedNode.data,
                      label: `${selectedNode.data.label} Copy`,
                    },
                  };
                  setNodes((nds) => [...nds, newNode]);
                  toast('Node duplicated', { icon: '📋' });
                }}
                className="flex-1 py-2 px-3 bg-blue-500/20 border border-blue-500/30 rounded-lg text-blue-400 hover:bg-blue-500/30 transition-colors text-sm font-medium"
              >
                Copy
              </motion.button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className="h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex flex-col">
      {/* Global Flow Styles Injection */}
      <div dangerouslySetInnerHTML={{ __html: globalFlowStyles }} />

      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              rotate: 360,
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 20 + i * 5,
              repeat: Infinity,
              ease: 'linear',
            }}
          >
            <BsRobot size={30 + i * 5} className="text-purple-500/10" />
          </motion.div>
        ))}
      </div>

      {/* Main Content Area */}
      <div className="flex flex-1 min-h-0">
        {/* Node Palette */}
        <AnimatePresence>
          {showNodePanel && (
            <motion.div
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              className="w-80 p-6 relative z-10 flex-shrink-0 overflow-y-auto"
              style={{
                background: 'rgba(15, 23, 42, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRight: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <h2 className="text-2xl font-bold text-white mb-6 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Flow Elements
              </h2>

              <div className="space-y-4">
                {nodeTemplates.map((template) => (
                  <div
                    key={template.type}
                    draggable
                    onDragStart={(event) => onDragStart(event, template.type)}
                    className={`p-4 rounded-lg cursor-grab active:cursor-grabbing bg-gradient-to-r ${template.color} opacity-80 hover:opacity-100 hover:scale-105 transition-all duration-200`}
                  >
                    <div className="flex items-center gap-3 text-white">
                      {template.icon}
                      <div>
                        <div className="font-semibold">{template.label}</div>
                        <div className="text-xs opacity-75">{template.description}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="mt-8 space-y-3">
                <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={simulateFlow}
                  className={`w-full p-3 rounded-lg flex items-center gap-2 transition-colors ${
                    isPlaying 
                      ? 'bg-red-500 hover:bg-red-600' 
                      : 'bg-green-500 hover:bg-green-600'
                  } text-white font-medium`}
                >
                  {isPlaying ? <FiPause size={20} /> : <FiPlay size={20} />}
                  {isPlaying ? 'Stop Simulation' : 'Test Flow'}
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={saveFlow}
                  className="w-full p-3 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium flex items-center gap-2"
                >
                  <FiSave size={20} />
                  Save Flow
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={loadFlow}
                  className="w-full p-3 rounded-lg bg-purple-500 hover:bg-purple-600 text-white font-medium flex items-center gap-2"
                >
                  <FiUpload size={20} />
                  Load Flow
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={clearFlow}
                  className="w-full p-3 rounded-lg bg-gray-600 hover:bg-gray-700 text-white font-medium flex items-center gap-2"
                >
                  <FiPlus size={20} className="rotate-45" />
                  Clear All
                </motion.button>
              </div>

              {/* Flow Statistics */}
              <div className="mt-8 p-4 rounded-lg bg-gray-800/50 border border-gray-700">
                <h4 className="text-white font-semibold mb-3">Flow Stats</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-gray-300">
                    <span>Nodes:</span>
                    <span className="text-blue-400">{backendStatus?.nodes ?? nodes.length}</span>
                  </div>
                  <div className="flex justify-between text-gray-300">
                    <span>Connections:</span>
                    <span className="text-green-400">{backendStatus?.connections ?? edges.length}</span>
                  </div>
                  <div className="flex justify-between text-gray-300">
                    <span>Status:</span>
                    <span className={(backendStatus?.status === 'RUNNING' || isPlaying) ? 'text-green-400' : 'text-gray-400'}>
                      {isPlaying ? 'Running' : (backendStatus?.status || 'Idle')}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Flow Canvas Area - Contains both canvas and console */}
        <div className="flex-1 flex flex-col relative">
          {/* Header */}
          <div className="absolute top-0 left-0 right-0 z-20 p-4">
            <div className="flex items-center justify-between">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowNodePanel(!showNodePanel)}
                className="p-2 rounded-lg bg-gray-800/50 backdrop-blur-sm text-white hover:bg-gray-700/50"
              >
                <FiSettings size={20} />
              </motion.button>

              <div className="flex items-center gap-4">
                <div className="px-4 py-2 rounded-lg bg-gray-800/50 backdrop-blur-sm text-white">
                  <span className="text-sm">
                    <span className="text-gray-400">Nodes:</span> {nodes.length} | 
                    <span className="text-gray-400"> Edges:</span> {edges.length}
                  </span>
                </div>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowConsole(!showConsole)}
                  className={`p-2 rounded-lg backdrop-blur-sm text-white transition-colors ${
                    showConsole ? 'bg-purple-500/50 hover:bg-purple-500/70' : 'bg-gray-800/50 hover:bg-gray-700/50'
                  }`}
                >
                  <FiTerminal size={16} />
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={exportFlow}
                  className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium flex items-center gap-2"
                >
                  <FiDownload size={16} />
                  Export
                </motion.button>
              </div>
            </div>
          </div>

          {/* React Flow Canvas */}
          <div className="flex-1 w-full" ref={reactFlowWrapper} style={{ height: showConsole ? `calc(100vh - ${consoleHeight}px - 120px)` : 'calc(100vh - 120px)' }}>
            <ReactFlowProvider>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onInit={setReactFlowInstance}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onNodeClick={onNodeClick}
                onPaneClick={onPaneClick}
                nodeTypes={nodeTypes}
                fitView
                connectionMode={ConnectionMode.Loose}
                defaultEdgeOptions={{
                  animated: true,
                  style: { 
                    stroke: 'var(--color-border-primary)', 
                    strokeWidth: 2,
                  },
                  type: 'smoothstep',
                }}
                style={{
                  background: 'transparent',
                }}
                defaultViewport={{ x: 0, y: 0, zoom: 1 }}
              >
                <Background 
                  color="var(--color-border-primary)" 
                  gap={20} 
                  style={{ opacity: 0.3 }}
                  variant={"dots" as any}
                />
                <Controls 
                  style={{
                    position: 'absolute',
                    bottom: 16,
                    left: 16,
                    background: 'rgba(15, 23, 42, 0.9)',
                    border: '1px solid var(--color-border-primary)',
                    borderRadius: 'var(--radius-lg)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                    zIndex: 10
                  }}
                />
                <MiniMap 
                  style={{ 
                    position: 'absolute', 
                    right: 16, 
                    bottom: 16,
                    background: 'rgba(15, 23, 42, 0.9)',
                    border: '1px solid var(--color-border-primary)',
                    borderRadius: 'var(--radius-lg)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                    overflow: 'hidden',
                    zIndex: 10
                  }}
                  nodeColor={() => 'var(--color-primary)'}
                  nodeStrokeColor={() => 'var(--color-border-primary)'}
                  maskColor="rgba(30, 41, 59, 0.8)"
                />
                
                {/* Welcome Message */}
                {nodes.length === 0 && (
                  <Panel position="top-center">
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center p-8 rounded-2xl bg-gray-800/50 backdrop-blur-sm border border-gray-700"
                    >
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                        className="inline-block mb-4"
                      >
                        <BiBrain size={48} className="text-purple-400" />
                      </motion.div>
                      <h3 className="text-2xl font-bold text-white mb-2">
                        Build Your Conversation Flow
                      </h3>
                      <p className="text-gray-400 mb-4">
                        Drag and drop elements from the left panel to create intelligent conversation flows
                      </p>
                      <div className="text-sm text-gray-500">
                        Start by dragging a "User Message" node onto the canvas
                      </div>
                    </motion.div>
                  </Panel>
                )}
              </ReactFlow>
              <Toolbar
                onZoomIn={() => reactFlowInstance?.zoomIn?.()}
                onZoomOut={() => reactFlowInstance?.zoomOut?.()}
                onFitView={() => reactFlowInstance?.fitView?.()}
              />
            </ReactFlowProvider>
          </div>

          {/* Console - Now positioned inside the main flow area */}
          <Console />
        </div>

        {/* Properties Panel */}
        <PropertiesPanel />
      </div>
    </div>
  );
};

export default FlowBuilder;